import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { api } from '../../api.js';

interface JsonPropertyDiffProps {
  depotPath: string;
  leftRev: number;
  rightRev: number;
}

export function JsonPropertyDiff({
  depotPath,
  leftRev,
  rightRev,
}: JsonPropertyDiffProps) {
  const leftQ = useQuery({
    queryKey: ['dam.preview.json', depotPath, leftRev],
    queryFn: () => fetchJson(depotPath, leftRev),
  });
  const rightQ = useQuery({
    queryKey: ['dam.preview.json', depotPath, rightRev],
    queryFn: () => fetchJson(depotPath, rightRev),
  });

  if (leftQ.isLoading || rightQ.isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Fetching property dumps…</span>
      </div>
    );
  }
  if (leftQ.error || rightQ.error) {
    const err = (leftQ.error ?? rightQ.error) as Error;
    return (
      <Alert tone="destructive">
        <AlertDescription>{err.message}</AlertDescription>
      </Alert>
    );
  }
  if (leftQ.data === null || rightQ.data === null) {
    return (
      <Alert>
        <AlertDescription>
          One or both revisions have no property dump cached. Open each
          revision once to populate the cache, then re-try.
        </AlertDescription>
      </Alert>
    );
  }

  return <DiffBody left={leftQ.data} right={rightQ.data} />;
}

async function fetchJson(path: string, rev: number): Promise<unknown | null> {
  const res = await fetch(
    `${api.damPreviewUrl(path, rev, 'json')}`,
    { credentials: 'include' },
  );
  if (res.status === 200) {
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('json')) {
      const body = await res.json();
      if (typeof body === 'object' && body && 'noPreview' in body) return null;
      return body;
    }
  }
  if (res.status === 202) return null;
  throw new Error(`preview.json fetch failed: HTTP ${res.status}`);
}

type DiffKind = 'equal' | 'added' | 'removed' | 'changed';

interface DiffNode {
  key: string | number;
  kind: DiffKind;
  leftValue?: unknown;
  rightValue?: unknown;
  children?: DiffNode[];
  shape: 'leaf' | 'object' | 'array';
}

function diffJson(left: unknown, right: unknown, key: string | number = '$'): DiffNode {
  const leftKind = shapeOf(left);
  const rightKind = shapeOf(right);

  if (left === undefined && right !== undefined) {
    return { key, kind: 'added', rightValue: right, shape: rightKind };
  }
  if (right === undefined && left !== undefined) {
    return { key, kind: 'removed', leftValue: left, shape: leftKind };
  }

  if (leftKind === 'leaf' && rightKind === 'leaf') {
    if (deepEqualPrimitive(left, right)) {
      return { key, kind: 'equal', leftValue: left, rightValue: right, shape: 'leaf' };
    }
    return { key, kind: 'changed', leftValue: left, rightValue: right, shape: 'leaf' };
  }

  if (leftKind !== rightKind) {
    return { key, kind: 'changed', leftValue: left, rightValue: right, shape: rightKind };
  }

  if (leftKind === 'array' && rightKind === 'array') {
    const l = left as unknown[];
    const r = right as unknown[];
    const len = Math.max(l.length, r.length);
    const children: DiffNode[] = [];
    for (let i = 0; i < len; i++) {
      children.push(diffJson(l[i], r[i], i));
    }
    const anyChanged = children.some((c) => c.kind !== 'equal');
    return {
      key,
      kind: anyChanged ? 'changed' : 'equal',
      children,
      shape: 'array',
    };
  }

  const l = left as Record<string, unknown>;
  const r = right as Record<string, unknown>;
  const keys = new Set<string>([...Object.keys(l), ...Object.keys(r)]);
  const sorted = [...keys].sort();
  const children: DiffNode[] = sorted.map((k) => diffJson(l[k], r[k], k));
  const anyChanged = children.some((c) => c.kind !== 'equal');
  return {
    key,
    kind: anyChanged ? 'changed' : 'equal',
    children,
    shape: 'object',
  };
}

function shapeOf(v: unknown): 'leaf' | 'object' | 'array' {
  if (v === null || v === undefined) return 'leaf';
  if (Array.isArray(v)) return 'array';
  if (typeof v === 'object') return 'object';
  return 'leaf';
}

function deepEqualPrimitive(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (Number.isNaN(a) && Number.isNaN(b)) return true;
  return false;
}

function DiffBody({ left, right }: { left: unknown; right: unknown }) {
  const tree = useMemo(() => diffJson(left, right), [left, right]);
  const stats = useMemo(() => tallyStats(tree), [tree]);
  if (stats.added + stats.removed + stats.changed === 0) {
    return (
      <Alert>
        <AlertDescription>
          Property dumps are identical between these revisions.
        </AlertDescription>
      </Alert>
    );
  }
  const total = stats.added + stats.removed + stats.changed;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <span className="text-xs text-green-400">+{stats.added}</span>
        <span className="text-xs text-red-400">−{stats.removed}</span>
        <span className="text-xs text-yellow-400">~{stats.changed}</span>
        <span className="text-xs text-muted-foreground">
          ({total} change{total === 1 ? '' : 's'})
        </span>
      </div>
      <div className="max-h-[70vh] overflow-auto rounded-md border border-neutral-800 bg-background p-2 font-mono text-xs">
        <DiffTreeNode node={tree} depth={0} isLast />
      </div>
    </div>
  );
}

function tallyStats(n: DiffNode): { added: number; removed: number; changed: number } {
  const acc = { added: 0, removed: 0, changed: 0 };
  function walk(node: DiffNode): void {
    if (node.children && node.children.length > 0) {
      for (const c of node.children) walk(c);
      return;
    }
    if (node.kind === 'added') acc.added++;
    else if (node.kind === 'removed') acc.removed++;
    else if (node.kind === 'changed') acc.changed++;
  }
  walk(n);
  return acc;
}

function DiffTreeNode({
  node,
  depth,
  isLast: _isLast,
}: {
  node: DiffNode;
  depth: number;
  isLast: boolean;
}) {
  const isContainer = !!node.children;
  const [open, setOpen] = useState<boolean>(
    depth === 0 || node.kind !== 'equal',
  );
  const keyLabel = typeof node.key === 'number' ? `[${node.key}]` : node.key;

  if (!isContainer) {
    return <DiffLeaf node={node} depth={depth} keyLabel={keyLabel} />;
  }

  const tint = bgFor(node.kind);
  return (
    <div style={{ background: tint }}>
      <button
        type="button"
        style={{ paddingLeft: depth * 14 }}
        className={`block w-full cursor-pointer bg-transparent text-left ${colorFor(node.kind)} focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="text-muted-foreground">{open ? '▾ ' : '▸ '}</span>
        <span>{keyLabel}</span>
        <span className="text-muted-foreground">
          {' '}
          {node.shape === 'array'
            ? `[${node.children!.length}]`
            : `{${node.children!.length}}`}
          {node.kind !== 'equal' && (
            <span className="ml-1.5 italic"> · {node.kind}</span>
          )}
        </span>
      </button>
      {open &&
        node.children!.map((c, i) => (
          <DiffTreeNode
            key={`${c.key}-${i}`}
            node={c}
            depth={depth + 1}
            isLast={i === node.children!.length - 1}
          />
        ))}
    </div>
  );
}

function DiffLeaf({
  node,
  depth,
  keyLabel,
}: {
  node: DiffNode;
  depth: number;
  keyLabel: string;
}) {
  const tint = bgFor(node.kind);
  return (
    <div
      style={{ paddingLeft: depth * 14, background: tint }}
      className={`overflow-x-auto whitespace-pre ${colorFor(node.kind)}`}
    >
      <span>{keyLabel}: </span>
      {node.kind === 'changed' ? (
        <>
          <span style={{ background: 'rgba(248, 81, 73, 0.25)' }} className="px-0.5">
            {formatValue(node.leftValue)}
          </span>
          <span className="text-muted-foreground"> → </span>
          <span style={{ background: 'rgba(46, 160, 67, 0.25)' }} className="px-0.5">
            {formatValue(node.rightValue)}
          </span>
        </>
      ) : node.kind === 'added' ? (
        <span>{formatValue(node.rightValue)}</span>
      ) : node.kind === 'removed' ? (
        <span className="line-through">{formatValue(node.leftValue)}</span>
      ) : (
        <span>{formatValue(node.leftValue)}</span>
      )}
    </div>
  );
}

function bgFor(kind: DiffKind): string {
  switch (kind) {
    case 'added':   return 'rgba(46, 160, 67, 0.10)';
    case 'removed': return 'rgba(248, 81, 73, 0.10)';
    case 'changed': return 'rgba(210, 153, 34, 0.10)';
    default:        return 'transparent';
  }
}

function colorFor(kind: DiffKind): string {
  switch (kind) {
    case 'added':   return 'text-green-400';
    case 'removed': return 'text-red-400';
    case 'changed': return 'text-yellow-400';
    default:        return 'text-foreground';
  }
}

function formatValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '—';
  if (typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v).slice(0, 120);
}
